import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// ============================================
// DATE & TIME UTILITIES
// ============================================

/**
 * Format date to Spanish locale (e.g., "5 ene 2026")
 * Uses local date parsing to avoid timezone issues with YYYY-MM-DD strings
 */
// ISO UTC → "22 oct 2026, 17:45" en hora de Ciudad de México
export function formatDateTimeMX(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format date range (e.g., "5 ene 2026 - 11 ene 2026")
 */
export function formatDateRange(startDate, endDate) {
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

// ============================================
// BIMESTER UTILITIES
// ============================================

const BIMESTER_NAMES = {
  1: 'Ene-Feb',
  2: 'Mar-Abr',
  3: 'May-Jun',
  4: 'Jul-Ago',
  5: 'Sep-Oct',
  6: 'Nov-Dic',
};

/**
 * Get bimester display name
 */
export function getBimesterName(bimester) {
  return BIMESTER_NAMES[bimester] || '';
}

// Trimestral: mapeo de trimestre a nombre
const QUARTER_NAMES = {
  1: 'T1 (Ene-Mar)',
  2: 'T2 (Abr-Jun)',
  3: 'T3 (Jul-Sep)',
  4: 'T4 (Oct-Dic)',
};

/**
 * Get quarter display name
 */
export function getQuarterName(quarter) {
  return QUARTER_NAMES[quarter] || '';
}

const MONTH_NAMES_ES = {
  1: 'Enero', 2: 'Febrero', 3: 'Marzo', 4: 'Abril', 5: 'Mayo', 6: 'Junio',
  7: 'Julio', 8: 'Agosto', 9: 'Septiembre', 10: 'Octubre', 11: 'Noviembre', 12: 'Diciembre',
};

export function getMonthName(month) {
  return MONTH_NAMES_ES[month] || '';
}

/**
 * Get the display name for a goal period based on goal_periodicity
 */
export function getGoalPeriodName(goalPeriodicity, periodNum) {
  if (goalPeriodicity === 'quarterly') {
    return getQuarterName(periodNum);
  }
  if (goalPeriodicity === 'monthly') {
    return getMonthName(periodNum);
  }
  return getBimesterName(periodNum);
}

/**
 * Get the number of goal periods per year
 */
export function getGoalPeriodCount(goalPeriodicity) {
  if (goalPeriodicity === 'quarterly') return 4;
  if (goalPeriodicity === 'monthly') return 12;
  return 6;
}

/**
 * Get the term label for goal periods (Bimestre/Trimestre/Mes)
 */
export function getGoalPeriodTermLabel(goalPeriodicity) {
  if (goalPeriodicity === 'quarterly') return 'Trimestre';
  if (goalPeriodicity === 'monthly') return 'Mes';
  return 'Bimestre';
}

// ============================================
// STATUS CONFIGURATION (Single Source of Truth)
// ============================================

/**
 * Centralized status configuration for UI consistency
 * Used by: Dashboard, KPIDetailView, Capture, etc.
 */
export const STATUS_CONFIG = {
  success: {
    label: 'Cumplido',
    color: 'status-success',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    iconColor: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    filterBadgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    // Shading for result cells
    resultBg: 'bg-emerald-500/15',
    resultBgDark: 'bg-emerald-500/25',
  },
  error: {
    label: 'No cumplido',
    color: 'status-error',
    badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
    iconColor: 'text-rose-500',
    bgColor: 'bg-rose-100',
    filterBadgeClass: 'bg-rose-100 text-rose-800 border-rose-300',
    // Shading for result cells
    resultBg: 'bg-rose-500/15',
    resultBgDark: 'bg-rose-500/25',
  },
  pending: {
    label: 'Pendiente',
    color: 'status-pending',
    badgeClass: 'bg-slate-100 text-slate-600 border-slate-200',
    iconColor: 'text-slate-400',
    bgColor: 'bg-slate-100',
    filterBadgeClass: 'bg-slate-100 text-slate-800 border-slate-300',
    // Shading for result cells (neutral)
    resultBg: 'bg-slate-500/10',
    resultBgDark: 'bg-slate-500/15',
  },
};

// Goal cell shading (always neutral gray)
export const GOAL_CELL_BG = 'bg-slate-100/50';
export const GOAL_CELL_BG_DARK = 'bg-slate-200/60';

/**
 * Get result cell background class based on status
 */
export function getResultCellBg(status, isDark = false) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return isDark ? config.resultBgDark : config.resultBg;
}

/**
 * Get status color class
 */
export function getStatusColor(status) {
  return STATUS_CONFIG[status]?.color || STATUS_CONFIG.pending.color;
}

/**
 * Get status text label
 */
export function getStatusText(status) {
  return STATUS_CONFIG[status]?.label || STATUS_CONFIG.pending.label;
}

/**
 * Get status badge CSS classes
 */
export function getStatusBadgeClass(status) {
  return STATUS_CONFIG[status]?.badgeClass || STATUS_CONFIG.pending.badgeClass;
}

/**
 * Get status filter badge CSS classes
 */
export function getStatusFilterBadgeClass(status) {
  return STATUS_CONFIG[status]?.filterBadgeClass || STATUS_CONFIG.pending.filterBadgeClass;
}

// ============================================
// NUMBER FORMATTING
// ============================================

/**
 * Format number with thousands separator
 * @param {number} num - Number to format
 * @param {number} decimals - Maximum decimal places (default: 2)
 */
export function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined) return '-';
  if (Number.isInteger(num)) {
    return num.toLocaleString('es-MX');
  }
  return num.toLocaleString('es-MX', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals 
  });
}

/**
 * Format number for chart axis display (compact with K, M abbreviations)
 * Handles large numbers by abbreviating them for better readability
 * @param {number} num - Number to format
 */
export function formatAxisNumber(num) {
  if (num === null || num === undefined || isNaN(num)) return '';
  
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  
  if (absNum >= 1000000) {
    // Millions: 1.5M, 2M
    const formatted = (absNum / 1000000).toFixed(absNum % 1000000 === 0 ? 0 : 1);
    return sign + formatted.replace('.0', '') + 'M';
  } else if (absNum >= 1000) {
    // Thousands: 1.5K, 250K
    const formatted = (absNum / 1000).toFixed(absNum % 1000 === 0 ? 0 : 1);
    return sign + formatted.replace('.0', '') + 'K';
  } else if (absNum < 1 && absNum > 0) {
    // Small decimals: show 2 decimal places
    return sign + absNum.toFixed(2);
  } else {
    // Small integers or zero
    return sign + Math.round(absNum).toString();
  }
}

/**
 * Format percentage
 */
export function formatPercentage(num) {
  if (num === null || num === undefined) return '-';
  return `${num.toFixed(2)}%`;
}

// ============================================
// UNIT & EVALUATION TYPE LABELS
// ============================================

const UNIT_LABELS = {
  pares: 'Pares',
  pesos: 'Pesos',
  unidades: 'Unidades',
  piezas: 'Piezas',
  porcentaje: '%',
  horas: 'Horas',
  dias: 'Días',
};

/**
 * Get unit display label
 */
export function getUnitLabel(unit) {
  return UNIT_LABELS[unit] || unit;
}

/**
 * Get evaluation type label
 */
export function getEvaluationTypeLabel(type) {
  switch (type) {
    case 'percentage':
      return 'Cumplimiento porcentual (Mayor es mejor)';
    case 'threshold':
      return 'Comparación contra meta (Menor es mejor)';
    default:
      return type;
  }
}

// ============================================
// PERIODICITY UTILITIES
// ============================================

const PERIODICITY_LABELS = {
  weekly: 'Semanal',
  monthly: 'Mensual',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
};

const PERIODICITY_DESCRIPTIONS = {
  weekly: 'Evaluación cada semana (jueves a miércoles)',
  monthly: 'Evaluación mensual (mes calendario)',
  bimonthly: 'Evaluación cada dos meses (Ene-Feb, Mar-Abr, etc.)',
  quarterly: 'Evaluación trimestral (T1: Ene-Mar, T2: Abr-Jun, T3: Jul-Sep, T4: Oct-Dic)',
  semiannual: 'Evaluación semestral (Ene-Jun, Jul-Dic)',
  annual: 'Evaluación anual (todo el año)',
};

// Bucket counts per periodicity
const PERIODICITY_BUCKET_COUNTS = {
  weekly: 52,
  monthly: 12,
  bimonthly: 6,
  quarterly: 4,
  semiannual: 2,
  annual: 1,
};

/**
 * Get periodicity display label
 */
export function getPeriodicityLabel(periodicity) {
  return PERIODICITY_LABELS[periodicity] || periodicity;
}

/**
 * Get periodicity description
 */
export function getPeriodicityDescription(periodicity) {
  return PERIODICITY_DESCRIPTIONS[periodicity] || '';
}

/**
 * Get the number of buckets for a periodicity
 */
export function getBucketCountForPeriodicity(periodicity) {
  return PERIODICITY_BUCKET_COUNTS[periodicity] || 52;
}

/**
 * Get all periodicity options for select
 */
export function getPeriodicityOptions() {
  return Object.entries(PERIODICITY_LABELS).map(([value, label]) => ({
    value,
    label,
    description: PERIODICITY_DESCRIPTIONS[value],
    bucketCount: PERIODICITY_BUCKET_COUNTS[value],
  }));
}

// ============================================
// PERIOD UTILITIES
// ============================================

/**
 * Check if period is editable (considering role)
 */
export function isPeriodEditable(period, isAdmin = false) {
  if (!period) return false;
  if (isAdmin) return true;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const editDeadline = new Date(period.edit_deadline);
  editDeadline.setHours(23, 59, 59, 999);
  
  return today <= editDeadline;
}

/**
 * Check if period is closed (past edit deadline) - independent of role
 */
export function isPeriodClosed(period) {
  if (!period) return true;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const editDeadline = new Date(period.edit_deadline);
  editDeadline.setHours(23, 59, 59, 999);
  
  return today > editDeadline;
}

/**
 * Check if period is in the future (hasn't started yet)
 * A period is future if today < period.start_date
 */
export function isPeriodFuture(period) {
  if (!period) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(period.start_date);
  startDate.setHours(0, 0, 0, 0);
  
  return today < startDate;
}

/**
 * Get period status: 'past', 'current', 'future', or 'editable'
 */
export function getPeriodStatus(period, isAdmin = false) {
  if (!period) return 'past';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(period.start_date);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(period.end_date);
  endDate.setHours(23, 59, 59, 999);
  
  const editDeadline = new Date(period.edit_deadline);
  editDeadline.setHours(23, 59, 59, 999);
  
  // Future period - hasn't started yet
  if (today < startDate) {
    return 'future';
  }
  
  // Current period - within start and end dates
  if (today >= startDate && today <= endDate) {
    return 'current';
  }
  
  // Past period - check edit deadline
  if (today <= editDeadline || isAdmin) {
    return 'editable';
  }
  
  return 'past';
}

// ============================================
// KPI CALCULATION UTILITIES (Single Source of Truth)
// ============================================

/**
 * Performance thresholds for status calculation (binary: cumple / no cumple)
 */
const PERFORMANCE_THRESHOLDS = {
  SUCCESS: 100,  // >= 100% = Cumplido
                 // < 100% = No cumplido
};

/**
 * Calculate KPI status based on result, goal and evaluation type
 * @returns {'success' | 'error' | 'pending'}
 */
export function calculateKPIStatus(result, goal, evaluationType = 'percentage') {
  if (result === null || result === undefined || goal === null || goal === undefined || goal === 0) {
    return 'pending';
  }
  
  let performance;
  if (evaluationType === 'percentage') {
    performance = (result / goal) * 100;
  } else {
    // threshold type: lower is better
    performance = (goal / result) * 100;
  }
  
  if (performance >= PERFORMANCE_THRESHOLDS.SUCCESS) return 'success';
  return 'error';
}

/**
 * Calculate performance value
 * @returns {number | null | string} Performance percentage or status string for threshold type
 */
export function calculatePerformance(result, goal, evaluationType) {
  if (goal === 0 || result === null || result === undefined) {
    return null;
  }
  
  if (evaluationType === 'percentage') {
    return (result / goal) * 100;
  }
  
  // For threshold type, return status string
  if (result === 0 || result <= goal) return 'success';
  return 'error';
}

/**
 * Get performance display object with text and status
 */
export function getPerformanceDisplay(result, goal, evaluationType) {
  if (result === null || result === undefined) {
    return { text: 'Sin capturar', status: 'pending' };
  }
  
  if (goal === 0) {
    return { text: 'Sin meta', status: 'pending' };
  }
  
  if (evaluationType === 'percentage') {
    const perf = (result / goal) * 100;
    const status = perf >= PERFORMANCE_THRESHOLDS.SUCCESS ? 'success' : 'error';
    return { text: `${perf.toFixed(2)}%`, status };
  }
  
  // Threshold type
  if (result === 0 || result <= goal) {
    return { text: 'Cumplido', status: 'success' };
  }
  return { text: 'No cumplido', status: 'error' };
}

// ============================================
// RANGE GOAL UTILITIES ("Meta por rango")
// ============================================

/**
 * Format a goal range object as "min – max"
 */
export function formatGoalRange(range) {
  if (!range || range.min === null || range.min === undefined || range.max === null || range.max === undefined) {
    return '-';
  }
  return `${formatNumber(range.min)} – ${formatNumber(range.max)}`;
}

/**
 * Check if a value is inside an inclusive [min, max] range
 * @returns {boolean|null} null if value/range missing
 */
export function isInRange(value, range) {
  if (value === null || value === undefined || !range || range.min === null || range.min === undefined || range.max === null || range.max === undefined) {
    return null;
  }
  return value >= range.min && value <= range.max;
}

/**
 * Range status for a single captured value: 'success' | 'error' | 'pending'
 */
export function calculateRangeStatus(value, range) {
  const inside = isInRange(value, range);
  if (inside === null) return 'pending';
  return inside ? 'success' : 'error';
}

/**
 * Overall range performance based on the MOST RECENT captured period.
 * Mirrors the "latest period" criterion used for the other goal types, but
 * compares the value against the KPI's configured range (goal_range.min/max).
 * Green if the latest captured result is inside its range; red if outside.
 * @returns {{ performance: string|null, status: string }}
 */
export function calculateRangeOverall(periods, fallbackRange = null) {
  if (!periods || periods.length === 0) return { performance: null, status: 'pending' };
  const captured = periods.filter((p) => p.result !== null && p.result !== undefined);
  if (captured.length === 0) return { performance: null, status: 'pending' };
  // Most recent captured period = highest period_number (fallback: last in array)
  const latest = captured.reduce((a, b) => {
    const an = a.period_number ?? 0;
    const bn = b.period_number ?? 0;
    return bn >= an ? b : a;
  });
  const range = latest.goal_range || fallbackRange;
  const inside = isInRange(latest.result, range);
  if (inside === null) return { performance: null, status: 'pending' };
  return inside
    ? { performance: 'En rango', status: 'success' }
    : { performance: 'Fuera de rango', status: 'error' };
}/**
 * Calculate KPI summary totals based on calculation type
 * @param {Array} periods - Array of period data with goal and result
 * @param {string} calculationType - 'average', 'accumulated', or 'fixed_per_period'
 * @param {boolean} isHierarchical - If KPI has hierarchy (periods already contain aggregated values)
 * @returns {{ totalGoal: number, totalResult: number, capturedCount: number, totalPeriods: number }}
 */
export function calculateKPISummary(periods, calculationType = 'accumulated', isHierarchical = false) {
  if (!periods || periods.length === 0) {
    return { totalGoal: 0, totalResult: 0, capturedCount: 0, totalPeriods: 0 };
  }

  const periodsWithResults = periods.filter(p => p.result !== null && p.result !== undefined);
  const capturedCount = periodsWithResults.length;
  const totalPeriods = periods.length;

  let totalGoal, totalResult;

  if (calculationType === 'range') {
    // Meta por rango: no hay meta numérica; resultado representativo = promedio de capturados
    totalGoal = 0;
    totalResult = capturedCount > 0
      ? periodsWithResults.reduce((sum, p) => sum + (p.result || 0), 0) / capturedCount
      : 0;
  } else if (calculationType === 'average') {
    // For "average" type: 
    // - For NON-hierarchical: goal is the fixed target (same for all periods), result is average of captured
    // - For hierarchical: each period.goal and period.result are already averages of leaf nodes
    //   So we need to average those period averages (weighted equally since each period represents one data point)
    const allGoals = periods.map(p => p.goal || 0).filter(g => g > 0);
    totalGoal = allGoals.length > 0 ? allGoals[0] : 0; // Fixed goal (all should be same for average type)
    
    if (isHierarchical) {
      // For hierarchical with average: each period's result is already an average of leaf nodes
      // We average across captured periods
      totalResult = capturedCount > 0
        ? periodsWithResults.reduce((sum, p) => sum + (p.result || 0), 0) / capturedCount
        : 0;
    } else {
      // For non-hierarchical with average: average all results
      totalResult = capturedCount > 0
        ? periodsWithResults.reduce((sum, p) => sum + (p.result || 0), 0) / capturedCount
        : 0;
    }
  } else if (calculationType === 'fixed_per_period') {
    // For "fixed_per_period" (límite fijo) type:
    // - Goal: The fixed limit (constant for each period)
    // - Result: AVERAGE of captured results (to compare against the fixed limit)
    // Each period is evaluated individually against the fixed limit
    const allGoals = periods.map(p => p.goal || 0).filter(g => g > 0);
    totalGoal = allGoals.length > 0 ? allGoals[0] : 0; // Límite fijo (igual para todos los periodos)
    
    // Resultado = PROMEDIO de resultados capturados (para comparar contra el límite)
    totalResult = capturedCount > 0
      ? periodsWithResults.reduce((sum, p) => sum + (p.result || 0), 0) / capturedCount
      : 0;
  } else {
    // For "accumulated" type: sum all goals and results
    // This works the same for hierarchical and non-hierarchical
    // (for hierarchical, period values are sums of leaf nodes, so summing periods is correct)
    totalGoal = periods.reduce((sum, p) => sum + (p.goal || 0), 0);
    totalResult = periods.reduce((sum, p) => sum + (p.result || 0), 0);
  }

  return { totalGoal, totalResult, capturedCount, totalPeriods };
}

/**
 * Calculate overall KPI performance and status
 * @returns {{ performance: number | string | null, status: string }}
 */
export function calculateOverallPerformance(totalResult, totalGoal, capturedCount, evaluationType) {
  if (capturedCount === 0 || totalGoal === 0) {
    return { performance: null, status: 'pending' };
  }

  if (evaluationType === 'percentage') {
    const performance = (totalResult / totalGoal) * 100;
    const status = performance >= PERFORMANCE_THRESHOLDS.SUCCESS ? 'success' : 'error';
    return { performance, status };
  }

  // Threshold type
  if (totalResult <= totalGoal) {
    return { performance: 'Cumplido', status: 'success' };
  }
  return { performance: 'No cumplido', status: 'error' };
}
